import { Paper, Typography, Stack } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface LegalDocCardProps {
  title: string;
  description: string;
  href: string;
  external?: boolean;
  tag?: string;
}

export function LegalDocCard({
  title,
  description,
  href,
  external = false,
  tag,
}: LegalDocCardProps) {
  const externalProps = external
    ? { target: '_blank' as const, rel: 'noopener noreferrer' }
    : {};

  return (
    <Paper
      component="a"
      href={href}
      {...externalProps}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 4,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: 'primary.light',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {tag && (
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            mb: 1,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: 0.5,
          }}
        >
          {tag}
        </Typography>
      )}
      <Typography
        variant="h4"
        component="h3"
        sx={{
          fontWeight: 700,
          mb: 1,
          fontSize: { xs: '1.125rem', md: '1.25rem' },
          lineHeight: 1.3,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
          flexGrow: 1,
          lineHeight: 1.6,
        }}
      >
        {description}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.9rem' }}
      >
        <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
          {external ? 'Open' : 'Read'}
        </Typography>
        {external ? (
          <OpenInNewIcon sx={{ fontSize: '1rem' }} />
        ) : (
          <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
        )}
      </Stack>
    </Paper>
  );
}
